import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BlockType, ListStyle } from '../../config';
import './slash-command-menu.scss';

// =======================================
// INTERFACES
// =======================================

interface BlockOption {
  type: BlockType;
  label: string;
  description: string;
  icon: string;
  category: 'text' | 'list' | 'media' | 'layout';
  shortcut?: string;
  headingLevel?: 1 | 2 | 3;
  listStyle?: ListStyle;
}

interface SlashCommandMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: BlockType, variant?: { headingLevel?: number; listStyle?: ListStyle }) => void;
  position: { top: number; left: number };
  searchQuery: string;
  /**
   * Restricts what "/" can insert here. Used inside an instruction item, whose
   * content is paragraphs, bullets and headings -- a table or a photo appendix
   * nested in a numbered instruction is not a thing the form has.
   */
  allowedBlockTypes?: BlockType[];
}

// =======================================
// BLOCK OPTIONS
// =======================================

const BLOCK_OPTIONS: BlockOption[] = [
  // Text blocks
  { type: 'paragraph', label: 'Text', description: 'Just start writing with plain text', icon: 'T', category: 'text' },
  { type: 'heading', label: 'Heading 1', description: 'Large section heading', icon: 'H₁', category: 'text', shortcut: '#', headingLevel: 1 },
  { type: 'heading', label: 'Heading 2', description: 'Medium section heading', icon: 'H₂', category: 'text', shortcut: '##', headingLevel: 2 },
  { type: 'heading', label: 'Heading 3', description: 'Small section heading', icon: 'H₃', category: 'text', shortcut: '###', headingLevel: 3 },
  
  // List blocks
  { type: 'bullet_list', label: 'Bulleted list', description: 'Create a simple bullet list', icon: '•', category: 'list', shortcut: '-', listStyle: 'bullet' },
  { type: 'bullet_list', label: 'Numbered list', description: 'A list numbered 1. 2. 3.', icon: '1.', category: 'list', listStyle: 'number' },
  { type: 'bullet_list', label: 'Lettered list', description: 'A list lettered a. b. c.', icon: 'a.', category: 'list', listStyle: 'letter' },
  { type: 'scope_list', label: 'To-do list', description: 'Interactive checklist for scope items', icon: '☐', category: 'list', shortcut: '[]' },
  { type: 'table', label: 'Table', description: 'Add a table for structured data', icon: '⊞', category: 'list' },
  
  // Media blocks
  { type: 'image', label: 'Image', description: 'Upload or embed an image', icon: '🖼', category: 'media' },
  { type: 'image_appendix', label: 'Image grid', description: 'Grid of numbered images with captions', icon: '⊟', category: 'media' },
  
  // Layout blocks
  { type: 'divider', label: 'Divider', description: 'Visual separator between sections', icon: '—', category: 'layout', shortcut: '---' },
  { type: 'spacer', label: 'Spacer', description: 'Add vertical spacing', icon: '↕', category: 'layout' },
  { type: 'signature', label: 'Signature', description: 'Document signature block', icon: '✍', category: 'layout' },
];

// =======================================
// MAIN COMPONENT
// =======================================

const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({
  isOpen,
  onClose,
  onSelect,
  position,
  searchQuery,
  allowedBlockTypes
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  // Filter options based on search query
  const filteredOptions = BLOCK_OPTIONS.filter(option => {
    if (allowedBlockTypes && !allowedBlockTypes.includes(option.type)) return false;
    const query = searchQuery.toLowerCase();
    return (
      option.label.toLowerCase().includes(query) ||
      option.description.toLowerCase().includes(query) ||
      option.type.toLowerCase().includes(query)
    );
  });

  // Reset selection when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[selectedIndex]) {
          const option = filteredOptions[selectedIndex];
          onSelect(option.type, { headingLevel: option.headingLevel, listStyle: option.listStyle });
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [isOpen, filteredOptions, selectedIndex, onSelect, onClose]);

  // Add keyboard listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = menuRef.current?.querySelector('.option-item.selected');
    selectedElement?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  // Group options by category
  const groupedOptions = filteredOptions.reduce((acc, option, index) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push({ ...option, originalIndex: index });
    return acc;
  }, {} as Record<string, (BlockOption & { originalIndex: number })[]>);

  const categoryLabels: Record<string, string> = {
    text: 'Text',
    list: 'Lists & Tables',
    media: 'Media',
    layout: 'Layout'
  };

  return (
    <div 
      ref={menuRef}
      className="slash-command-menu"
      style={{ top: position.top, left: position.left }}
    >
      <div className="menu-header">
        <span className="menu-title">Basic blocks</span>
      </div>
      
      {filteredOptions.length === 0 ? (
        <div className="no-results">
          No blocks matching "{searchQuery}"
        </div>
      ) : (
        <div className="menu-options">
          {Object.entries(groupedOptions).map(([category, options]) => (
            <div key={category} className="option-group">
              {category !== 'text' && (
                <div className="group-label">{categoryLabels[category]}</div>
              )}
              {options.map((option) => (
                <div
                  key={`${option.type}-${option.label}`}
                  className={`option-item ${option.originalIndex === selectedIndex ? 'selected' : ''}`}
                  onClick={() => onSelect(option.type, { headingLevel: option.headingLevel, listStyle: option.listStyle })}
                  onMouseEnter={() => setSelectedIndex(option.originalIndex)}
                >
                  <span className="option-icon">{option.icon}</span>
                  <div className="option-text">
                    <span className="option-label">{option.label}</span>
                  </div>
                  {option.shortcut && (
                    <span className="option-shortcut">{option.shortcut}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Search area at bottom - Notion style */}
      <div className="menu-search">
        <span className="search-icon">/</span>
        <span className="search-text">
          {searchQuery ? (
            <>Type to search<span className="search-value">: {searchQuery}</span></>
          ) : (
            'Type to search'
          )}
        </span>
      </div>
      
      <div className="menu-footer">
        <span><kbd>↑</kbd><kbd>↓</kbd> to navigate</span>
        <span><kbd>↵</kbd> select</span>
        <span><kbd>esc</kbd> close</span>
      </div>
    </div>
  );
};

export default SlashCommandMenu;
